import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  StickyNote,
  Plus,
  Save,
  Trash2,
  Paperclip,
  Lock,
} from 'lucide-react';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { dbg } from '@/lib/dbg';

interface CycleNotesModalProps {
  cycleId: number | null;
  isOpen: boolean;
  locked: boolean;
  onClose: () => void;
}

type Attachment = { url: string; name?: string };

type NoteDto = {
  id: string;
  title: string;
  bodyHtml: string;
  attachments: Attachment[];
  createdAt?: string;
  updatedAt?: string;
};

const normalizeAttachments = (attachments: any): Attachment[] => {
  if (!attachments) return [];
  if (Array.isArray(attachments)) {
    return attachments.map((a: any) => {
      if (typeof a === 'string') {
        return { url: a, name: a.split('/').pop() || 'attachment' };
      }
      if (a && typeof a === 'object') {
        return { url: a.url ?? '', name: a.name ?? a.filename ?? a.url ?? 'attachment' };
      }
      return { url: '', name: 'attachment' };
    }).filter(a => a.url);
  }
  return [];
};

export function CycleNotesModal({ cycleId, isOpen, locked, onClose }: CycleNotesModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);

  const { data: notes = [], isLoading } = useQuery<NoteDto[]>({
    queryKey: ['cycle-notes', cycleId],
    enabled: isOpen && !!cycleId,
    queryFn: async () => {
      const res = await apiRequest(`/api/cycles/${cycleId}/notes`, 'GET');
      if (!res.ok) {
        throw new Error('Failed to fetch notes');
      }
      const payload = await res.json();
      return (payload || []).map((note: any) => ({
        id: note.id,
        title: note.title ?? '',
        bodyHtml: note.bodyHtml ?? note.body_html ?? '',
        attachments: normalizeAttachments(note.attachments),
        createdAt: note.createdAt ?? note.created_at,
        updatedAt: note.updatedAt ?? note.updated_at,
      }));
    },
  });

  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [notes]);

  // Get the first note ID as a stable reference (only changes when notes actually change)
  const firstNoteId = sortedNotes.length > 0 ? sortedNotes[0].id : null;


  useEffect(() => {
    if (!isOpen) {
      setSelectedNoteId(null);
      setTitle('');
      setBodyHtml('');
      setAttachments([]);
      return;
    }
    // Only auto-select first note if no note is currently selected
    // This prevents re-selecting on every render and avoids infinite loops
    if (firstNoteId && !selectedNoteId) {
      const firstNote = sortedNotes[0];
      setSelectedNoteId(firstNote.id);
      setTitle(firstNote.title);
      setBodyHtml(firstNote.bodyHtml);
      setAttachments(firstNote.attachments || []);
    } else if (!firstNoteId && selectedNoteId) {
      // Notes list is now empty, reset
      setSelectedNoteId(null);
      setTitle('');
      setBodyHtml('');
      setAttachments([]);
    }
  }, [isOpen, firstNoteId]);


  const resetToNote = (note: NoteDto | null) => {
    setSelectedNoteId(note?.id ?? null);
    setTitle(note?.title ?? '');
    setBodyHtml(note?.bodyHtml ?? '');
    setAttachments(note?.attachments ?? []);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/api/cycles/${cycleId}/notes`, 'POST', {
        title,
        bodyHtml,
        attachments,
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to create note');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-notes', cycleId] });
      toast({ title: 'Note created', description: 'Cycle note saved successfully.' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create note', description: error?.message || 'Unexpected error', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const res = await apiRequest(`/api/cycles/${cycleId}/notes/${noteId}`, 'PUT', {
        title,
        bodyHtml,
        attachments,
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to update note');
      }
      return res.json();
    },
    onSuccess: (_data, noteId) => {
      queryClient.invalidateQueries({ queryKey: ['cycle-notes', cycleId] });
      setSelectedNoteId(noteId);
      toast({ title: 'Note updated', description: 'Changes saved.' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update note', description: error?.message || 'Unexpected error', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const res = await apiRequest(`/api/cycles/${cycleId}/notes/${noteId}`, 'DELETE');
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to delete note');
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-notes', cycleId] });
      toast({ title: 'Note deleted', description: 'The note has been removed.' });
      resetToNote(sortedNotes.find(n => n.id !== selectedNoteId) ?? null);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to delete note', description: error?.message || 'Unexpected error', variant: 'destructive' });
    },
  });

  const handleSave = () => {
    if (!title.trim()) {
      toast({ title: 'Title required', description: 'Please provide a note title.', variant: 'destructive' });
      return;
    }
    if (locked) {
      return;
    }
    if (selectedNoteId && sortedNotes.some(n => n.id === selectedNoteId)) {
      updateMutation.mutate(selectedNoteId);
    } else {
      createMutation.mutate();
    }
  };

  const handleAddNote = () => {
    setSelectedNoteId(null);
    setTitle('');
    setBodyHtml('');
    setAttachments([]);
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'cycle-notes');
      if (cycleId) {
        formData.append('cycleId', String(cycleId));
      }
      const uploadResponse = await apiRequest('/api/uploads/direct', 'POST', formData);
      if (!uploadResponse.ok) {
        const text = await uploadResponse.text();
        throw new Error(text || 'Upload failed');
      }
      const uploadData = await uploadResponse.json();
      const url = uploadData.signedUrl || uploadData.path;
      const next = [...attachments, { url, name: file.name }];
      setAttachments(next);
      toast({ title: 'Attachment uploaded', description: file.name });
    } catch (error: any) {
      dbg.error?.('[CYCLE_NOTES_UPLOAD]', error?.message);
      toast({ title: 'Upload failed', description: error?.message || 'Unable to upload file', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    if (locked) return;
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const currentNote = useMemo(() => {
    if (!selectedNoteId) return null;
    return sortedNotes.find(n => n.id === selectedNoteId) ?? null;
  }, [selectedNoteId, sortedNotes]);

  return (
    <Dialog open={isOpen} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Cycle Notes
          </DialogTitle>
          <DialogDescription>
            Add and manage notes for this cycle. Notes become read-only once the cycle is locked.
          </DialogDescription>
        </DialogHeader>

        {locked && (
          <Alert variant="destructive">
            <Lock className="h-4 w-4" />
            <AlertDescription>
              This cycle is locked. Notes are read-only; new notes and edits are disabled.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4 border rounded-md">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="text-sm font-medium">Notes</div>
              {!locked && (
                <Button size="sm" variant="secondary" onClick={handleAddNote}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Note
                </Button>
              )}
            </div>
            <ScrollArea className="h-[420px]">
              {isLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading notes...</div>
              ) : sortedNotes.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No notes yet.</div>
              ) : (
                <div className="divide-y">
                  {sortedNotes.map((note) => (
                    <button
                      key={note.id}
                      className="w-full text-left px-4 py-3 hover:bg-muted/70 focus:bg-muted/70"
                      onClick={() => resetToNote(note)}
                    >
                      <div className="font-medium text-sm truncate">{note.title || 'Untitled note'}</div>
                      <div className="text-xs text-muted-foreground">
                        {note.createdAt ? format(new Date(note.createdAt), 'MMM d, yyyy') : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="col-span-8 border rounded-md p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="note-title">Title</Label>
              <Input
                id="note-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Note title"
                disabled={locked}
              />
            </div>

            <div className="space-y-2">
              <Label>Body</Label>
              <div className={locked ? 'pointer-events-none opacity-70' : ''}>
                <RichTextEditor
                  content={bodyHtml}
                  onChange={locked ? () => { } : setBodyHtml}
                  placeholder="Write note details..."
                  className="min-h-[160px]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Attachments</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  disabled={locked || uploading}
                  onChange={(e) => handleUpload(e.target.files?.[0] || null)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((att, idx) => (
                  <Badge
                    key={`${att.url}-${idx}`}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    <Paperclip className="h-3 w-3" />
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {att.name || 'Attachment'}
                    </a>
                    {!locked && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => handleRemoveAttachment(idx)}
                      >
                        ×
                      </Button>
                    )}
                  </Badge>
                ))}
                {attachments.length === 0 && (
                  <div className="text-sm text-muted-foreground">No attachments</div>
                )}
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {currentNote?.updatedAt && (
                  <>Last updated {format(new Date(currentNote.updatedAt), 'MMM d, yyyy p')}</>
                )}
              </div>
              <div className="flex gap-2">
                {!locked && currentNote && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => currentNote?.id && deleteMutation.mutate(currentNote.id)}
                    disabled={deleteMutation.isPending || updateMutation.isPending || createMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                )}
                {!locked && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
